import React, { useEffect, useMemo, useRef, useState } from "react";
import moment from "moment-timezone";
import { markPhotoForInvoice } from "api/Projects";
import { themevals } from "theme/themevals";

/**
 * ProjectPhotoIncludePicker — A18 (Session 57)
 *
 * Chip + popover that folds project Photos-tab images into the same
 * invoice-level include surface as the site-map snapshot picker. Sits
 * next to SnapshotIncludePicker in the invoice toolbar. Each photo's
 * `invoiceInclude` flag is the existing per-photo boolean on
 * project_photo, toggled through the existing
 * markPhotoForInvoice endpoint — no new persistence, no migration. The
 * invoice PDF already renders `invoiceInclude` photos inline (A19), so
 * this is purely the selection surface; removing the duplicate checkbox
 * on the Photos tab is the other half of A18.
 *
 * Props:
 *   projectId: the project whose photos we toggle
 *   photos:    already-fetched project photos (from the invoice Overview)
 *   onChanged: called after a successful toggle so the parent can refetch
 *              (keeps the inline PDF render + count in sync)
 */

interface ProjectPhoto {
  id: string;
  url: string;
  invoiceInclude?: boolean;
  createdAt?: string;
  uploadedBy?: { firstName?: string; lastName?: string } | null;
}

interface Props {
  projectId: string;
  photos: ProjectPhoto[];
  onChanged?: () => void;
}

const styles: Record<string, React.CSSProperties> = {
  wrapper: {
    position: "relative",
    display: "inline-flex",
    alignItems: "center",
    userSelect: "none",
  },
  chip: {
    display: "inline-flex",
    alignItems: "center",
    gap: "6px",
    padding: "4px 10px",
    borderRadius: 999,
    fontSize: 12,
    fontWeight: 500,
    cursor: "pointer",
    transition: "all 0.15s ease",
    whiteSpace: "nowrap",
  },
  chipInactive: {
    background: "transparent",
    color: themevals.colors.dkgrey,
    border: "1px solid " + themevals.colors.inputOutline,
  },
  chipActive: {
    background: "#E6F1FB",
    color: "#0C447C",
    border: "1px solid #85B7EB",
  },
  countBadge: {
    background: "#185FA5",
    color: "#fff",
    padding: "1px 7px",
    borderRadius: 999,
    fontSize: 10,
    fontWeight: 500,
    lineHeight: 1.4,
  },
  popover: {
    position: "absolute",
    top: "calc(100% + 10px)",
    left: 0,
    zIndex: 100,
    background: "#fff",
    border: "1px solid #E4E7EB",
    borderRadius: 12,
    padding: 16,
    boxShadow: "0 8px 24px rgba(0,0,0,0.12)",
    minWidth: 560,
    maxWidth: 720,
  },
  popoverTail: {
    position: "absolute",
    top: -7,
    left: 20,
    width: 12,
    height: 12,
    background: "#fff",
    borderLeft: "1px solid #E4E7EB",
    borderTop: "1px solid #E4E7EB",
    transform: "rotate(45deg)",
  },
  popoverHeader: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "baseline",
    marginBottom: 12,
  },
  popoverTitle: {
    margin: 0,
    fontSize: 13,
    fontWeight: 500,
    color: "#111",
    fontFamily: "Poppins, sans-serif",
  },
  popoverHint: {
    margin: 0,
    fontSize: 11,
    color: "#8A8B8F",
    fontFamily: "Poppins, sans-serif",
  },
  grid: {
    display: "grid",
    gridTemplateColumns: "repeat(auto-fill, minmax(140px, 1fr))",
    gap: 12,
  },
  card: {
    borderRadius: 8,
    overflow: "hidden",
    cursor: "pointer",
    transition: "border-color 0.15s ease, opacity 0.15s ease",
    background: "#F9FAFB",
  },
  cardSelected: {
    border: "2px solid #185FA5",
    opacity: 1,
  },
  cardUnselected: {
    border: "0.5px solid #E4E7EB",
    opacity: 0.85,
  },
  thumb: {
    aspectRatio: "4 / 3",
    background: "#E4E7EB",
    position: "relative",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    fontSize: 11,
    color: "#78716C",
  },
  thumbImg: {
    width: "100%",
    height: "100%",
    objectFit: "cover",
    display: "block",
  },
  checkBadge: {
    position: "absolute",
    top: 6,
    right: 6,
    width: 22,
    height: 22,
    background: "#185FA5",
    color: "#fff",
    borderRadius: "50%",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    fontSize: 13,
    fontWeight: 600,
    pointerEvents: "none",
  },
  cardFoot: {
    padding: "5px 8px",
    fontSize: 10,
    color: "#5D5E62",
    fontFamily: "Poppins, sans-serif",
  },
  footer: {
    marginTop: 12,
    paddingTop: 12,
    borderTop: "0.5px solid #E4E7EB",
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
  },
  footerNote: {
    margin: 0,
    fontSize: 11,
    color: "#5D5E62",
    fontFamily: "Poppins, sans-serif",
  },
  saveError: {
    marginTop: 8,
    padding: "6px 10px",
    background: "#FEE2E2",
    border: "1px solid #FCA5A5",
    borderRadius: 4,
    fontSize: 11,
    color: "#991B1B",
  },
};

const ProjectPhotoIncludePicker: React.FC<Props> = ({ projectId, photos, onChanged }) => {
  const [open, setOpen] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);
  const [pendingId, setPendingId] = useState<string | null>(null);
  // Local optimistic copy so the grid reflects a toggle instantly; the
  // parent refetch (onChanged) is the source of truth on next render.
  const [local, setLocal] = useState<ProjectPhoto[]>(photos);
  const wrapperRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    setLocal(photos);
  }, [photos]);

  // Click-outside-to-close.
  useEffect(() => {
    if (!open) return;
    const handler = (e: MouseEvent) => {
      const wrapper = wrapperRef.current;
      if (!wrapper) return;
      if (!wrapper.contains(e.target as Node)) setOpen(false);
    };
    const timer = setTimeout(() => document.addEventListener("mousedown", handler), 0);
    return () => {
      clearTimeout(timer);
      document.removeEventListener("mousedown", handler);
    };
  }, [open]);

  const selectedCount = useMemo(() => local.filter((p) => p.invoiceInclude).length, [local]);
  const totalCount = local.length;
  const active = selectedCount > 0;

  const toggle = async (photo: ProjectPhoto) => {
    if (pendingId) return;
    const newValue = !photo.invoiceInclude;
    setPendingId(photo.id);
    setSaveError(null);
    // Optimistic.
    setLocal((prev) => prev.map((p) => (p.id === photo.id ? { ...p, invoiceInclude: newValue } : p)));
    const res = await markPhotoForInvoice({ id: projectId, data: { include: newValue, photoId: photo.id } });
    setPendingId(null);
    if (res?.success) {
      onChanged?.();
    } else {
      // Revert on failure.
      setLocal((prev) => prev.map((p) => (p.id === photo.id ? { ...p, invoiceInclude: !newValue } : p)));
      setSaveError("Could not update this photo. Try again.");
    }
  };

  // Nothing to show when the project has no photos.
  if (!projectId || totalCount === 0) return null;

  return (
    <div ref={wrapperRef} style={styles.wrapper}>
      <div
        style={{ ...styles.chip, ...(active ? styles.chipActive : styles.chipInactive) }}
        onClick={() => setOpen((o) => !o)}
        title={selectedCount + " of " + totalCount + " project photos selected for the invoice"}
      >
        <span>Include photos</span>
        <span style={styles.countBadge}>
          {selectedCount} / {totalCount}
        </span>
      </div>

      {open && (
        <div style={styles.popover} onMouseDown={(e) => e.stopPropagation()}>
          <div style={styles.popoverTail} />
          <div style={styles.popoverHeader}>
            <p style={styles.popoverTitle}>Project photos</p>
            <p style={styles.popoverHint}>Click to include on the invoice</p>
          </div>

          <div style={styles.grid}>
            {local.map((photo) => {
              const selected = !!photo.invoiceInclude;
              return (
                <div
                  key={photo.id}
                  style={{
                    ...styles.card,
                    ...(selected ? styles.cardSelected : styles.cardUnselected),
                    cursor: pendingId ? "wait" : "pointer",
                  }}
                  onClick={(e) => {
                    e.stopPropagation();
                    toggle(photo);
                  }}
                >
                  <div style={styles.thumb}>
                    <img src={photo.url} alt="Project photo" style={styles.thumbImg} />
                    {selected && <span style={styles.checkBadge}>{"\u2713"}</span>}
                  </div>
                  <div style={styles.cardFoot}>
                    {photo.createdAt ? moment(photo.createdAt).format("MMM D, YYYY") : ""}
                  </div>
                </div>
              );
            })}
          </div>

          <div style={styles.footer}>
            <p style={styles.footerNote}>
              {selectedCount === 0
                ? "No project photos will be included on the invoice."
                : "Selected photos appear below the invoice body, in upload order."}
            </p>
          </div>

          {saveError && <div style={styles.saveError}>{saveError}</div>}
        </div>
      )}
    </div>
  );
};

export default ProjectPhotoIncludePicker;
